import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ShieldCheck, MessageSquare, Users, FolderOpen, BookOpen, Landmark, Zap, ArrowRight, Inbox, Bell, BellOff, BellRing, Sparkles, TrendingUp, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { StatusBadge, formatDate } from "@/lib/qa";
import {
    isPushSupported,
    isInIframe,
    permission as pushPermission,
    getServerStatus as getPushStatus,
    requestNotificationPermission,
    registerPushSubscription,
    unsubscribeAdmin,
    sendTestPush,
} from "@/lib/push";

const QUICK_ACTIONS = [
    { to: "/admin/richieste", icon: MessageSquare, label: "Richieste" },
    { to: "/admin/utenti", icon: Users, label: "Utenti" },
    { to: "/admin/statistiche", icon: BarChart3, label: "Statistiche di utilizzo" },
    { to: "/admin/categorie", icon: FolderOpen, label: "Categorie" },
    { to: "/admin/faq", icon: Zap, label: "FAQ" },
    { to: "/admin/faq/sinonimi", icon: Sparkles, label: "Sinonimi" },
    { to: "/admin/faq/analitica", icon: TrendingUp, label: "Analitica ricerca" },
    { to: "/admin/articoli", icon: BookOpen, label: "Articoli" },
    { to: "/admin/risorse", icon: Landmark, label: "Risorse" },
];

export default function AdminDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ total: 0, received: 0, in_progress: 0, answered: 0 });
    const [users, setUsers] = useState({ total: 0 });
    const [articles, setArticles] = useState(0);
    const [faqs, setFaqs] = useState(0);
    const [pending, setPending] = useState([]);
    const [push, setPush] = useState({
        supported: false,
        loading: true,
        busy: false,
        permission: "default",
        configured: false,
        subscribed: false,
        iframe: false,
        secure: true,
    });

    const refreshPushStatus = async () => {
        const supported = isPushSupported();
        const iframe = isInIframe();
        const secure = typeof window !== "undefined" ? window.isSecureContext : true;
        if (!supported) {
            setPush(p => ({ ...p, supported: false, loading: false, iframe, secure }));
            return;
        }
        try {
            const s = await getPushStatus();
            setPush(p => ({
                ...p,
                supported: true,
                loading: false,
                iframe,
                secure,
                permission: pushPermission(),
                configured: !!s.configured,
                subscribed: !!(s.subscribed && s.hasBrowserSubscription),
            }));
        } catch (_e) {
            setPush(p => ({ ...p, supported: true, loading: false, iframe, secure, permission: pushPermission() }));
        }
    };

    useEffect(() => {
        API.get("/admin/questions/stats").then(({ data }) => setStats(data)).catch(() => {});
        API.get("/users").then(({ data }) => setUsers(data.counts || {})).catch(() => {});
        API.get("/admin/articles", { params: { status: "published" } }).then(({ data }) => setArticles(data.length)).catch(() => {});
        API.get("/admin/faqs", { params: { status: "published" } }).then(({ data }) => setFaqs(data.length)).catch(() => {});
        API.get("/admin/questions", { params: { status: "received" } }).then(({ data }) => setPending(data.slice(0, 5))).catch(() => {});
        refreshPushStatus();
    }, []);

    // NOTE: Notification.requestPermission() must be invoked FIRST in the
    // click handler — before any await — otherwise Chrome/Android may drop
    // the "user gesture" and silently refuse to show the permission popup.
    const onEnablePush = async () => {
        if (!isPushSupported()) {
            toast.error("Il tuo browser non supporta le notifiche push.");
            return;
        }
        if (isInIframe()) {
            toast.error("Apri la pagina in una scheda dedicata (non in un'anteprima) per attivare le notifiche.");
            return;
        }
        // 1) Fire the native popup IMMEDIATELY, still in the user-gesture task.
        const permPromise = requestNotificationPermission();
        setPush(p => ({ ...p, busy: true }));
        let result;
        try {
            result = await permPromise;
        } catch (_e) {
            result = "denied";
        }
        if (result === "denied") {
            toast.error("Permesso notifiche bloccato. Sblocca le notifiche per questo sito dalle impostazioni del browser e riprova.");
            setPush(p => ({ ...p, busy: false, permission: "denied" }));
            return;
        }
        if (result !== "granted") {
            // "default" or "dismissed": popup was shown but user closed it
            toast.info("Nessuna scelta effettuata. Riprova e conferma 'Consenti' nel popup del browser.");
            setPush(p => ({ ...p, busy: false, permission: pushPermission() }));
            return;
        }
        // 2) Now finalise the subscription (SW + push subscribe + backend).
        try {
            await registerPushSubscription();
            toast.success("Notifiche push attivate su questo dispositivo.");
            await refreshPushStatus();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Impossibile completare l'iscrizione alle notifiche.";
            toast.error(msg);
            setPush(p => ({ ...p, busy: false }));
        }
    };

    const onDisablePush = async () => {
        setPush(p => ({ ...p, busy: true }));
        try {
            await unsubscribeAdmin();
            toast.success("Notifiche disattivate su questo dispositivo.");
            await refreshPushStatus();
        } catch (_e) {
            toast.error("Impossibile disattivare le notifiche.");
        }
        setPush(p => ({ ...p, busy: false }));
    };

    const onTestPush = async () => {
        setPush(p => ({ ...p, busy: true }));
        try {
            const r = await sendTestPush();
            toast.success(`Notifica di prova inviata (${r.sent}/${r.total}).`);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Invio notifica di prova non riuscito.";
            toast.error(msg);
        }
        setPush(p => ({ ...p, busy: false }));
    };

    const KPIS = [
        { icon: Inbox, label: "Nuove richieste", value: stats.received, to: "/admin/richieste" },
        { icon: MessageSquare, label: "In lavorazione", value: stats.in_progress, to: "/admin/richieste" },
        { icon: Users, label: "Utenti registrati", value: users.total || 0, to: "/admin/utenti" },
        { icon: Zap, label: "FAQ pubblicate", value: faqs, to: "/admin/faq" },
        { icon: BookOpen, label: "Articoli pubblicati", value: articles, to: "/admin/articoli" },
    ];

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
            <div className="mb-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                        <ShieldCheck className="size-3.5" /> Pannello amministratore
                    </div>
                    <h1 data-testid="admin-title" className="mt-1 font-serif text-3xl lg:text-4xl font-semibold">Panoramica</h1>
                    <p className="mt-2 text-muted-foreground">Benvenuto, {user.full_name}.</p>
                </div>
                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10 uppercase tracking-widest text-[10px] font-semibold">Ruolo: Admin</Badge>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-8" data-testid="admin-kpis">
                {KPIS.map((k, i) => {
                    const Icon = k.icon;
                    return (
                        <Link key={i} to={k.to} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-primary/30 hover:shadow-md transition group" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
                            <div className="flex items-center justify-between">
                                <Icon className="size-5 text-primary/70" />
                                <ArrowRight className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
                            </div>
                            <div className="mt-3 text-4xl font-serif font-semibold text-slate-900">{k.value}</div>
                            <div className="mt-1 text-sm text-muted-foreground">{k.label}</div>
                        </Link>
                    );
                })}
            </div>

            <Card className="border-slate-200 mb-8">
                <CardContent className="py-5">
                    <div className="flex flex-wrap gap-2">
                        {QUICK_ACTIONS.map(({ to, icon: Icon, label }) => (
                            <Button key={to} asChild variant="outline" size="sm" data-testid={`quick-${label.toLowerCase()}`}>
                                <Link to={to}><Icon className="size-4 mr-2" />{label}</Link>
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <Card className="border-slate-200 mb-8" data-testid="admin-push-card">
                <CardContent className="py-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className={`shrink-0 rounded-xl p-2 ${push.subscribed ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-600"}`}>
                                {push.subscribed ? <BellRing className="size-5" /> : <Bell className="size-5" />}
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-slate-900">Notifiche push</div>
                                <div className="text-sm text-muted-foreground">
                                    {!push.supported && "Il tuo browser non supporta le notifiche push. Prova da Chrome/Android o Safari 16.4+/iOS."}
                                    {push.supported && !push.secure && "Le notifiche funzionano solo su connessioni sicure (HTTPS)."}
                                    {push.supported && push.secure && push.iframe && "Apri la pagina in una scheda dedicata per attivare le notifiche."}
                                    {push.supported && push.secure && !push.iframe && !push.configured && "Notifiche push non configurate sul server."}
                                    {push.supported && push.secure && !push.iframe && push.configured && push.permission === "denied" && "Permesso bloccato dal browser. Vai sull'icona del lucchetto accanto all'URL → Impostazioni del sito → Notifiche → Consenti, poi ricarica la pagina."}
                                    {push.supported && push.secure && !push.iframe && push.configured && push.permission !== "denied" && (
                                        push.subscribed
                                            ? "Attive su questo dispositivo. Riceverai un avviso ogni volta che arriva una nuova domanda."
                                            : "Attivale su questo telefono/browser per ricevere un avviso quando arriva una nuova domanda, anche ad app chiusa."
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            {push.supported && push.secure && !push.iframe && push.configured && push.permission !== "denied" && !push.subscribed && (
                                <Button
                                    onClick={onEnablePush}
                                    disabled={push.busy || push.loading}
                                    data-testid="push-enable-btn"
                                    className="bg-primary hover:bg-primary/90"
                                >
                                    <Bell className="size-4 mr-2" />
                                    {push.busy ? "Attivazione…" : "Attiva notifiche"}
                                </Button>
                            )}
                            {push.supported && push.secure && !push.iframe && push.configured && push.permission === "denied" && (
                                <Button
                                    variant="outline"
                                    onClick={refreshPushStatus}
                                    disabled={push.busy}
                                    data-testid="push-retry-btn"
                                >
                                    Ho sbloccato, ricontrolla
                                </Button>
                            )}
                            {push.supported && push.subscribed && (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={onTestPush}
                                        disabled={push.busy}
                                        data-testid="push-test-btn"
                                    >
                                        Prova
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={onDisablePush}
                                        disabled={push.busy}
                                        data-testid="push-disable-btn"
                                    >
                                        <BellOff className="size-4 mr-2" />
                                        Disattiva
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {pending.length > 0 && (
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Prossime da lavorare</div>
                    <div className="space-y-2" data-testid="admin-pending">
                        {pending.map(q => (
                            <Link key={q.id} to={`/admin/richieste/${q.id}`} className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-primary/30 transition">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <StatusBadge status={q.status} />
                                            {q.category && <span className="text-xs text-muted-foreground">Domanda</span>}
                                        </div>
                                        <div className="font-semibold text-slate-900 truncate">{q.title}</div>
                                        <div className="text-xs text-muted-foreground">{q.user_name} · {formatDate(q.created_at)}</div>
                                    </div>
                                    <ArrowRight className="size-4 text-muted-foreground" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
